import { Component, ElementRef, HostListener, computed, effect, inject, input, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import {
  Activity,
  BatteryCharging,
  Building2,
  ChevronDown,
  ChevronRight,
  ChevronsLeftRightEllipsis,
  Menu,
  ClipboardList,
  Database,
  Download,
  FilePlus2,
  FileText,
  FolderCheck,
  LayoutDashboard,
  LogOut,
  LucideAngularModule,
  ReceiptText,
  ScrollText,
  Users,
  Wallet,
  Zap,
  type LucideIconData,
} from 'lucide-angular';
import { AuthService } from '../core/services/auth.service';
import { MenuItem } from '../core/models/domain.models';
import { ApiService } from '../core/services/api.service';
import { VendorContextService } from '../core/services/vendor-context.service';
import { LayoutPreferencesService } from './layout-preferences.service';

type NavLinkItem = {
  type: 'link';
  label: string;
  link: string;
  icon: LucideIconData;
  exact?: boolean;
};

type NavGroupItem = {
  type: 'group';
  key: string;
  label: string;
  icon: LucideIconData;
  children: Array<{ label: string; link: string; icon: LucideIconData }>;
};

type NavItem = NavLinkItem | NavGroupItem;

type NavSection = {
  label: string;
  items: NavItem[];
};

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, LucideAngularModule],
  styles: [
    `
      :host {
        display: block;
      }

      .nav-group-toggle {
        width: 100%;
        border: 0;
        background: transparent;
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .nav-section {
        padding: 0.35rem;
        border-radius: 1rem;
        background: linear-gradient(180deg, rgba(244, 246, 253, 0.9) 0%, rgba(255, 255, 255, 0.95) 100%);
        border: 1px solid #eef2fb;
      }

      .nav-section + .nav-section {
        margin-top: 0.85rem;
      }

      .nav-link-child {
        font-size: 0.92rem;
      }

      .rotate-90 {
        transform: rotate(90deg);
      }

      .rotate-180 {
        transform: rotate(180deg);
      }

      .sidebar-collapsed {
        width: 88px;
        min-width: 88px;
      }

      .sidebar-collapsed .sidebar-copy,
      .sidebar-collapsed .section-label,
      .sidebar-collapsed .nav-chevron,
      .sidebar-collapsed .profile-copy,
      .sidebar-collapsed .logout-copy,
      .sidebar-collapsed .top-nav-divider {
        display: none;
      }

      .sidebar-collapsed .nav-link,
      .sidebar-collapsed .nav-group-toggle {
        justify-content: center;
        padding-inline: 0.5rem;
      }

      .sidebar-collapsed .brand-wrap,
      .sidebar-collapsed .profile-summary {
        justify-content: center;
      }

      .sidebar-top {
        position: sticky;
        top: 0;
        z-index: 1034;
        width: 100%;
        min-width: 0;
        height: auto;
        border-right: 0;
        border-bottom: 0;
        background: linear-gradient(180deg, #0d4d9a 0%, #0a4389 100%);
        box-shadow: 0 8px 20px rgba(10, 37, 84, 0.14);
        overflow: visible;
      }

      .top-nav {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        flex-wrap: wrap;
        padding: 0.12rem 0.35rem;
        
        overflow-y: visible;
        scrollbar-width: thin;
      }

      .top-nav-section {
        display: flex;
        align-items: center;
        gap: 0.15rem;
        flex-wrap: wrap;
      }

      .top-nav-divider {
        width: 1px;
        align-self: stretch;
        background: rgba(255, 255, 255, 0.22);
      }

      .top-nav .nav-link,
      .top-nav .nav-group-toggle {
        width: auto;
        min-height: 28px;
        border-radius: 0.2rem;
        padding: 0.38rem 0.55rem;
        color: #ffffff;
        font-size: 0.7rem;
        font-weight: 500;
        white-space: nowrap;
      }

      .top-nav-dropdown {
        position: relative;
      }

      .sidebar-top .top-nav-section:first-child {
        margin-right: 0.35rem;
        white-space: nowrap;
      }

      .sidebar-top .nav-link.active,
      .sidebar-top .nav-link:hover,
      .sidebar-top .nav-group-toggle.active,
      .sidebar-top .nav-group-toggle:hover {
        background: #1a66d4;
        color: #ffffff;
      }

      .section-label {
        padding-inline: 0.65rem;
        margin-bottom: 0.45rem;
      }

      .sidebar .nav-link,
      .sidebar .nav-group-toggle {
        min-height: 30px;
      }

      .sidebar .nav-link {
        transition:
          background-color 0.18s ease,
          color 0.18s ease,
          transform 0.18s ease;
      }

      .sidebar .nav-link:hover,
      .sidebar .nav-group-toggle:hover {
        transform: translateX(2px);
      }

      .sidebar-top .nav-link:hover,
      .sidebar-top .nav-group-toggle:hover {
        transform: none;
      }

      .profile-summary {
        padding: 0.35rem;
        border-radius: 0.9rem;
        background: #f8faff;
      }

      .drawer-brand-bar {
        min-height: 46px;
        padding: 0 0.55rem 0 0.45rem;
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: linear-gradient(180deg, #f6f8fc 0%, #dfe6ef 100%);
        border-bottom: 1px solid #cbd7e6;
      }

      .drawer-brand {
        display: inline-flex;
        align-items: center;
        gap: 0.45rem;
        min-width: 0;
      }

      .drawer-brand-mark {
        width: 13px;
        height: 18px;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 2px;
      }

      .drawer-brand-mark span {
        display: block;
        border-radius: 2px;
        background: linear-gradient(180deg, #2447ff 0%, #1b4fb7 100%);
      }

      .drawer-brand-mark span:nth-child(1) {
        height: 8px;
        margin-top: 5px;
      }

      .drawer-brand-mark span:nth-child(2),
      .drawer-brand-mark span:nth-child(3),
      .drawer-brand-mark span:nth-child(4) {
        height: 8px;
      }

      .drawer-brand-mark span:nth-child(4) {
        margin-top: -5px;
      }

      .drawer-brand-wordmark {
        font-size: 0.92rem;
        line-height: 1;
        letter-spacing: -0.04em;
        white-space: nowrap;
      }

      .drawer-brand-dark {
        font-weight: 700;
        color: #233247;
      }

      .drawer-brand-accent {
        font-weight: 600;
        font-style: italic;
        color: #274eff;
      }

      .drawer-toggle-btn {
        width: 30px;
        height: 30px;
        border: 0;
        border-radius: 0.4rem;
        background: transparent;
        color: #53657f;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .drawer-toggle-btn:hover {
        background: rgba(27, 79, 183, 0.08);
        color: #173f8e;
      }

      .sidebar-drawer {
        width: 100%;
        min-height: 100%;
        background: linear-gradient(180deg, #0f428f 0%, #0d3c86 100%);
        color: #ffffff;
        box-shadow: 14px 0 34px rgba(7, 24, 56, 0.24);
        overflow-y: auto;
      }

      .sidebar-drawer .sidebar-scroll {
        padding: 0.2rem 0 1rem;
      }

      .sidebar-drawer.sidebar-drawer-compact {
        overflow-x: hidden;
      }

      .sidebar-drawer .nav-section {
        padding: 0;
        border: 0;
        border-radius: 0;
        background: transparent;
      }

      .sidebar-drawer .nav-section + .nav-section {
        margin-top: 0;
      }

      .sidebar-drawer .section-label,
      .sidebar-drawer .profile-summary,
      .sidebar-drawer .logout-copy {
        display: none;
      }

      .sidebar-drawer.sidebar-drawer-compact .drawer-brand {
        display: none;
      }

      .sidebar-drawer.sidebar-drawer-compact .drawer-brand-bar {
        justify-content: center;
        padding-inline: 0;
      }

      .sidebar-drawer .nav-link,
      .sidebar-drawer .nav-group-toggle {
        min-height: 50px;
        padding: 0.78rem 1rem;
        border-radius: 0;
        color: rgba(255, 255, 255, 0.96);
        font-size: 0.95rem;
        font-weight: 500;
        border-bottom: 1px solid rgba(255, 255, 255, 0.07);
      }

      .sidebar-drawer .nav-link lucide-icon,
      .sidebar-drawer .nav-group-toggle lucide-icon {
        color: #ffffff;
      }

      .sidebar-drawer .nav-link:hover,
      .sidebar-drawer .nav-group-toggle:hover,
      .sidebar-drawer .nav-link.active,
      .sidebar-drawer .nav-group-toggle.active {
        transform: none;
        background: rgba(60, 125, 221, 0.9);
        color: #ffffff;
      }

      .sidebar-drawer .nav-chevron {
        opacity: 0.92;
      }

      .sidebar-drawer.sidebar-drawer-compact .sidebar-copy,
      .sidebar-drawer.sidebar-drawer-compact .nav-chevron,
      .sidebar-drawer.sidebar-drawer-compact .drawer-top-menu-text {
        display: none;
      }

      .sidebar-drawer.sidebar-drawer-compact .nav-link,
      .sidebar-drawer.sidebar-drawer-compact .nav-group-toggle {
        justify-content: center;
        padding-inline: 0;
      }

      .sidebar-drawer.sidebar-drawer-compact .nav-link lucide-icon,
      .sidebar-drawer.sidebar-drawer-compact .nav-group-toggle lucide-icon {
        margin: 0;
      }

      .sidebar-drawer .drawer-child-wrap {
        padding-left: 0;
        background: rgba(4, 23, 58, 0.22);
      }

      .sidebar-drawer .nav-link-child {
        min-height: 42px;
        padding-left: 2.9rem;
        font-size: 0.87rem;
        color: rgba(255, 255, 255, 0.86);
        border-bottom: 1px solid rgba(255, 255, 255, 0.05);
      }

      .sidebar-drawer .nav-link-child:hover,
      .sidebar-drawer .nav-link-child.active {
        background: rgba(255, 255, 255, 0.12);
        color: #ffffff;
      }

      .drawer-top-menu-action {
        margin: 0.9rem 0.8rem 0;
        padding-top: 0.85rem;
        border-top: 1px solid rgba(255, 255, 255, 0.12);
      }

      .drawer-top-menu-btn {
        width: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 0.45rem;
        min-height: 42px;
        border: 1px solid rgba(255, 255, 255, 0.24);
        border-radius: 0.5rem;
        background: rgba(255, 255, 255, 0.08);
        color: #ffffff;
        font-size: 0.84rem;
        font-weight: 600;
      }

      .drawer-top-menu-btn:hover {
        background: rgba(255, 255, 255, 0.15);
      }

      .sidebar-drawer.sidebar-drawer-compact .drawer-top-menu-action {
        margin: 0.65rem 0.35rem 0;
      }

      .sidebar-drawer.sidebar-drawer-compact .drawer-top-menu-btn {
        min-height: 40px;
        padding: 0;
        border-radius: 0.45rem;
      }

      .top-nav-panel {
        position: absolute;
        top: 100%;
        left: 0;
        z-index: 1;
        min-width: 240px;
        max-width: min(320px, calc(100vw - 2rem));
        max-height: calc(100vh - 96px);
        overflow-y: auto;
        border: 0;
        border-radius: 0 0 0.45rem 0.45rem;
        background: #0f468e;
        box-shadow: 0 16px 36px rgba(8, 27, 66, 0.28);
        padding: 0.35rem 0;
      }

      .top-nav-panel .nav-link {
        border-radius: 0.25rem;
        min-height: 36px;
        margin: 0 0.3rem;
        padding: 0.58rem 0.62rem;
        color: #ffffff;
        font-weight: 500;
        font-size: 0.82rem;
      }

      .top-nav-panel .nav-link.active,
      .top-nav-panel .nav-link:hover {
        background: rgba(255, 255, 255, 0.1);
        color: #ffffff;
      }

      .top-nav-panel .nav-link lucide-icon {
        display: none;
      }

      @media (max-width: 991.98px) {
        .sidebar-top {
          position: static;
        }

        .top-nav-divider {
          display: none;
        }
      }
    `,
  ],
  template: `
    @if (mode() === 'left') {
      <aside class="sidebar d-flex flex-column" [class.sidebar-collapsed]="collapsed()" [class.sidebar-drawer]="drawer()" [class.sidebar-drawer-compact]="drawer() && layout.topDrawerCompact()">
        @if (drawer()) {
          <div class="drawer-brand-bar">
            <div class="drawer-brand" aria-label="TradeYourAmp">
              <span class="drawer-brand-mark" aria-hidden="true">
                <span></span>
                <span></span>
                <span></span>
                <span></span>
              </span>
              <div class="drawer-brand-wordmark">
                <span class="drawer-brand-dark">TradeYour</span><span class="drawer-brand-accent">Amp</span>
              </div>
            </div>

            <button type="button" class="drawer-toggle-btn" [title]="layout.topDrawerCompact() ? 'Expand side menu' : 'Compact side menu'" (click)="toggleDrawerCompact()">
              <lucide-icon [img]="MenuIcon" [size]="16"></lucide-icon>
            </button>
          </div>
        } @else {
          <div class="px-4 py-3 border-bottom">
            <div class="brand-wrap d-flex align-items-center gap-3">
              <span class="brand-box"><lucide-icon [img]="ZapIcon" [size]="20"></lucide-icon></span>
              <div class="sidebar-copy">
                <div class="fw-bold">Power Trading</div>
                <div class="text-muted small">Bilateral Portal</div>
              </div>
            </div>
          </div>
        }

        <div class="px-3 py-3 sidebar-scroll" [class.px-0]="drawer()">
          @for (section of sections(); track section.label) {
            <div class="nav-section">
              @if (!drawer()) {
                <div class="section-label" [class.mt-3]="$index > 0">{{ section.label }}</div>
              }
              <nav class="nav flex-column gap-1" [class.gap-0]="drawer()">
                @for (item of section.items; track item.label) {
                  @if (item.type === 'link') {
                    <a
                      [routerLink]="item.link"
                      routerLinkActive="active"
                      [routerLinkActiveOptions]="item.exact ? { exact: true } : { exact: false }"
                      class="nav-link"
                      [attr.title]="collapsed() || (drawer() && layout.topDrawerCompact()) ? item.label : null"
                      (click)="handleNavLinkClick()"
                    >
                      <lucide-icon [img]="item.icon" [size]="18"></lucide-icon>
                      <span class="sidebar-copy">{{ item.label }}</span>
                    </a>
                  } @else {
                    <button
                      type="button"
                      class="nav-link nav-group-toggle"
                      [class.active]="isGroupActive(item) || isGroupOpen(item.key)"
                      [attr.title]="collapsed() || (drawer() && layout.topDrawerCompact()) ? item.label : null"
                      (click)="toggleGroup(item.key)"
                    >
                      <span class="d-inline-flex align-items-center gap-2">
                        <lucide-icon [img]="item.icon" [size]="18"></lucide-icon>
                        <span class="sidebar-copy">{{ item.label }}</span>
                      </span>
                      <lucide-icon
                        [img]="drawer() ? ChevronRightIcon : ChevronDownIcon"
                        [size]="16"
                        class="nav-chevron"
                        [class.rotate-90]="drawer() && isGroupOpen(item.key)"
                        [class.rotate-180]="!drawer() && isGroupOpen(item.key)"
                      ></lucide-icon>
                    </button>
                    @if (isGroupOpen(item.key) && !collapsed() && !(drawer() && layout.topDrawerCompact())) {
                      <div class="nav flex-column gap-1" [class.ps-4]="!drawer()" [class.gap-0]="drawer()" [class.drawer-child-wrap]="drawer()">
                        @for (child of item.children; track child.label) {
                          <a [routerLink]="child.link" routerLinkActive="active" class="nav-link nav-link-child" (click)="handleNavLinkClick()">
                            <lucide-icon [img]="child.icon" [size]="16"></lucide-icon>{{ child.label }}
                          </a>
                        }
                      </div>
                    }
                  }
                }
              </nav>
            </div>
          }
        </div>

        @if (drawer()) {
          <div class="drawer-top-menu-action mt-auto">
            <button type="button" class="drawer-top-menu-btn" [title]="layout.topDrawerCompact() ? 'Switch to top menu' : null" (click)="switchToTopMenu()">
              <lucide-icon [img]="LayoutSwitchIcon" [size]="16"></lucide-icon>
              <span class="drawer-top-menu-text">Switch to top menu</span>
            </button>
          </div>
        } @else {
          <div class="mt-auto border-top px-3 py-3">
            <div class="profile-summary d-flex align-items-center gap-2 mb-2 px-2">
              <span class="icon-pill" style="width:32px;height:32px;background:rgba(58,87,232,.12);color:#3a57e8;">
                {{ initials() }}
              </span>
              <div class="profile-copy min-w-0">
                <div class="small fw-semibold text-truncate">{{ auth.user()?.full_name || 'User' }}</div>
                @if (vendorContext.vendorName()) {
                  <div class="text-muted small text-truncate">{{ vendorContext.vendorName() }}</div>
                }
                <div class="text-muted small text-capitalize">{{ (auth.user()?.role || 'viewer').replace('_', ' ') }}</div>
              </div>
            </div>
            <button class="btn btn-light w-100 text-start" [class.text-center]="collapsed()" (click)="auth.logout()">
              <lucide-icon [img]="LogOutIcon" [size]="16"></lucide-icon>
              <span class="logout-copy ms-2">Sign Out</span>
            </button>
          </div>
        }
      </aside>
    } @else {
      <aside class="sidebar sidebar-top">
        <div class="top-nav">
          @for (section of sections(); track section.label) {
            <div class="top-nav-section">
              @for (item of section.items; track item.label) {
                @if (item.type === 'link') {
                  <a
                    [routerLink]="item.link"
                    routerLinkActive="active"
                    [routerLinkActiveOptions]="item.exact ? { exact: true } : { exact: false }"
                    class="nav-link"
                  >
                    <lucide-icon [img]="item.icon" [size]="16"></lucide-icon>{{ item.label }}
                  </a>
                } @else {
                  <div class="top-nav-dropdown">
                    <button
                      type="button"
                      class="nav-link nav-group-toggle"
                      [class.active]="isGroupActive(item) || isGroupOpen(item.key)"
                      (click)="toggleGroup(item.key)"
                    >
                      <span class="d-inline-flex align-items-center gap-2">
                        <lucide-icon [img]="item.icon" [size]="16"></lucide-icon>{{ item.label }}
                      </span>
                      <lucide-icon [img]="ChevronDownIcon" [size]="15" [class.rotate-180]="isGroupOpen(item.key)"></lucide-icon>
                    </button>
                    @if (isGroupOpen(item.key)) {
                      <div class="top-nav-panel">
                        <div class="nav flex-column gap-1">
                          @for (child of item.children; track child.label) {
                            <a [routerLink]="child.link" routerLinkActive="active" class="nav-link nav-link-child">
                              <lucide-icon [img]="child.icon" [size]="16"></lucide-icon>{{ child.label }}
                            </a>
                          }
                        </div>
                      </div>
                    }
                  </div>
                }
              }
            </div>
            @if (!$last) {
              <div class="top-nav-divider"></div>
            }
          }
        </div>
      </aside>
    }
  `,
})
export class SidebarComponent {
  protected readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  protected readonly vendorContext = inject(VendorContextService);
  private readonly router = inject(Router);
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  protected readonly layout = inject(LayoutPreferencesService);

  readonly mode = input<'left' | 'top'>('left');
  readonly collapsed = input(false);
  readonly drawer = input(false);

  protected readonly menuRows = signal<MenuItem[]>([]);
  protected readonly openGroups = signal<Record<string, boolean>>({});
  protected readonly isAdmin = computed(() => {
    const role = this.auth.user()?.role;
    return role === 'admin' || role === 'super_admin';
  });
  protected readonly isVendor = computed(() => this.auth.user()?.role === 'vendor');
  protected readonly initials = computed(() =>
    (this.auth.user()?.full_name || 'U')
      .split(' ')
      .filter(Boolean)
      .map((item) => item[0]?.toUpperCase())
      .join('')
      .slice(0, 2),
  );
  protected readonly sections = computed<NavSection[]>(() => {
    const sections = this.buildSectionsFromMenus(this.menuRows());
    if (!this.isVendor()) return sections;
    return this.ensureVendorProfileSection(sections);
  });

  protected readonly LayoutDashboardIcon = LayoutDashboard;
  protected readonly Building2Icon = Building2;
  protected readonly ChevronDownIcon = ChevronDown;
  protected readonly ChevronRightIcon = ChevronRight;
  protected readonly MenuIcon = Menu;
  protected readonly FolderCheckIcon = FolderCheck;
  protected readonly FileTextIcon = FileText;
  protected readonly ScrollTextIcon = ScrollText;
  protected readonly ClipboardListIcon = ClipboardList;
  protected readonly DownloadIcon = Download;
  protected readonly ReceiptTextIcon = ReceiptText;
  protected readonly FilePlus2Icon = FilePlus2;
  protected readonly WalletIcon = Wallet;
  protected readonly DatabaseIcon = Database;
  protected readonly ActivityIcon = Activity;
  protected readonly BatteryChargingIcon = BatteryCharging;
  protected readonly UsersIcon = Users;
  protected readonly LogOutIcon = LogOut;
  protected readonly LayoutSwitchIcon = ChevronsLeftRightEllipsis;
  protected readonly ZapIcon = Zap;

  constructor() {
    this.openGroups.set(this.initialOpenGroups());
    effect((onCleanup) => {
      const user = this.auth.user();
      if (!user) {
        this.menuRows.set([]);
        return;
      }
      const sub = this.api.get<MenuItem[]>('/api/navigation/menus').subscribe({
        next: (rows) => {
          this.menuRows.set(rows);
          this.openGroups.update((current) => ({ ...this.initialOpenGroups(), ...current }));
        },
        error: () => this.menuRows.set([]),
      });
      onCleanup(() => sub.unsubscribe());
    });
  }

  protected toggleGroup(key: string): void {
    if (this.drawer() && this.layout.topDrawerCompact()) {
      this.layout.toggleTopDrawerCompact();
    }
    this.openGroups.update((current) => ({ ...current, [key]: !current[key] }));
  }

  protected isGroupOpen(key: string): boolean {
    return !!this.openGroups()[key];
  }

  protected isGroupActive(item: NavGroupItem): boolean {
    return item.children.some((child) => this.router.url.startsWith(child.link));
  }

  protected handleNavLinkClick(): void {
    if (!this.drawer()) return;
    this.layout.closeTopDrawer();
    this.closeAllGroups();
  }

  protected toggleDrawerCompact(): void {
    if (!this.drawer()) return;
    this.closeAllGroups();
    this.layout.toggleTopDrawerCompact();
  }

  protected switchToTopMenu(): void {
    if (!this.drawer()) return;
    this.layout.closeTopDrawer();
    this.closeAllGroups();
  }

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    if (this.mode() !== 'top') return;

    const target = event.target;
    if (!(target instanceof Node)) return;
    if (this.elementRef.nativeElement.contains(target)) return;

    this.closeAllGroups();
  }

  private closeAllGroups(): void {
    this.openGroups.set({});
  }

  private initialOpenGroups(): Record<string, boolean> {
    const url = this.router.url;
    return {
      'master-setting': url.startsWith('/document-configurations') || url.startsWith('/charges'),
      'contract-management': url.startsWith('/contracts'),
      application: url.startsWith('/applications') || url.startsWith('/application-approvals'),
      billing:
        url.startsWith('/payment-knock-off') ||
        url.startsWith('/sop-tmr-billing') ||
        url.startsWith('/oa-billing') ||
        url.startsWith('/bill-verification') ||
        url.startsWith('/rea-matching') ||
        url.startsWith('/credit-debit-notes') ||
        url.startsWith('/rea-contract-analysis'),
      reports: url.startsWith('/bess/daily-performance-summary') || url.startsWith('/bess/profit-loss-report'),
      bess:
        url.startsWith('/bess/') &&
        !url.startsWith('/bess/daily-performance-summary') &&
        !url.startsWith('/bess/profit-loss-report'),
      'auto-fetch': url.startsWith('/auto-fetch/'),
      admin: url.startsWith('/staff') || url.startsWith('/menu-management') || url.startsWith('/menu-audit-logs'),
    };
  }

  private buildSectionsFromMenus(rows: MenuItem[]): NavSection[] {
    const byParent = new Map<string, MenuItem[]>();
    for (const row of rows) {
      const key = row.parent_id || '__root__';
      const current = byParent.get(key) || [];
      current.push(row);
      byParent.set(key, current);
    }
    for (const items of byParent.values()) {
      items.sort((a, b) => Number(a.menu_priority || 0) - Number(b.menu_priority || 0) || a.menu_name.localeCompare(b.menu_name));
    }

    const roots = byParent.get('__root__') || [];
    return roots.map((root) => ({
      label: root.menu_name,
      items: (byParent.get(root.id) || [])
        .map((row) => {
          const children = byParent.get(row.id) || [];
          if (children.length > 0) {
            return {
              type: 'group' as const,
              key: this.groupKeyFor(row),
              label: row.menu_name,
              icon: this.iconForMenu(row),
              children: children.map((child) => ({
                label: child.menu_name,
                link: this.resolveMenuLink(child.menu_url),
                icon: this.iconForMenu(child),
              })),
            };
          }
          return {
            type: 'link' as const,
            label: row.menu_name,
            link: this.resolveMenuLink(row.menu_url),
            icon: this.iconForMenu(row),
            exact: this.resolveMenuLink(row.menu_url) === '/',
          };
        })
        .filter((item) => item.type === 'group' || item.link !== '#'),
    }));
  }

  private ensureVendorProfileSection(sections: NavSection[]): NavSection[] {
    const vendorId = this.auth.user()?.vendor_id || '';
    if (!vendorId) return sections;

    const vendorProfileLink = `/vendors/${vendorId}`;
    const hasVendorProfile = sections.some((section) =>
      section.items.some((item) =>
        item.type === 'link'
          ? item.link === vendorProfileLink
          : item.children.some((child) => child.link === vendorProfileLink),
      ),
    );
    if (hasVendorProfile) return sections;

    const profileItem: NavLinkItem = {
      type: 'link',
      label: 'Vendor Profile',
      link: vendorProfileLink,
      icon: this.Building2Icon,
    };

    if (sections.length === 0) {
      return [{ label: 'Vendor', items: [profileItem] }];
    }

    const [first, ...rest] = sections;
    return [{ ...first, items: [profileItem, ...first.items] }, ...rest];
  }

  private resolveMenuLink(link: string): string {
    const vendorId = this.auth.user()?.vendor_id || '';
    const resolvedLink = (link || '').replaceAll('{vendor_id}', vendorId);
    if (resolvedLink === '/bill-verification' && !this.isAdmin()) {
      return '/bill-verification1';
    }
    return resolvedLink;
  }

  private groupKeyFor(row: MenuItem): string {
    return row.menu_alias || row.menu_name.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-');
  }

  private iconForMenu(row: Pick<MenuItem, 'menu_name' | 'menu_alias' | 'menu_url'>): LucideIconData {
    const key = `${row.menu_alias || ''} ${row.menu_name || ''} ${row.menu_url || ''}`.toLowerCase();
    if (key.includes('dashboard')) return this.LayoutDashboardIcon;
    if (key.includes('vendor')) return this.Building2Icon;
    if (key.includes('document') || key.includes('charge')) return this.FolderCheckIcon;
    if (key.includes('loa')) return this.FileTextIcon;
    if (key.includes('contract')) return this.ScrollTextIcon;
    if (key.includes('application')) return this.ClipboardListIcon;
    if (key.includes('billing') || key.includes('payment') || key.includes('credit') || key.includes('debit')) return this.WalletIcon;
    if (key.includes('verification') || key.includes('bill')) return this.ReceiptTextIcon;
    if (key.includes('matching') || key.includes('analysis')) return this.ActivityIcon;
    if (key.includes('bess') || key.includes('hedging') || key.includes('bid') || key.includes('discharge') || key.includes('battery')) {
      return this.BatteryChargingIcon;
    }
    if (key.includes('auto-fetch') || key.includes('forecast') || key.includes('grid india') || key.includes('all rea')) return this.DownloadIcon;
    if (key.includes('mcp') || key.includes('report')) return this.DatabaseIcon;
    if (key.includes('staff') || key.includes('admin')) return this.UsersIcon;
    if (key.includes('audit')) return this.ScrollTextIcon;
    return this.ClipboardListIcon;
  }
}
